import _mapValues from 'lodash.mapvalues';
import _keyBy from 'lodash.keyby';
import camelcaseKeys from 'camelcase-keys';
import { hasThresholdStyling, isCategorizedByStatus } from '@nbtsolutions/vetro-mapbox';
import {
  Layer,
  LayerStyle,
  LayerSymbol,
  CategorizedSymbolWithLayerInfo,
  FeatureStatus,
} from '@/types';

/**
 * Convert layer symbol properties to camelCase for mapbox.
 * This isn't handled by our axios interceptor, because the symbol
 * key must not have its case changed. Eventually, we may want to
 * do this in a response pipeline a la fibermap.
 */
export const formatLayers = (layers: Layer[]): Layer[] =>
  layers.map((layer) => {
    const symbols = _mapValues(layer.style.symbols, (symbol: LayerSymbol) => camelcaseKeys(symbol));

    const style = { ...layer.style, symbols };

    return { ...layer, style };
  });

/**
 * Attempt to apply a theme to a list of layers
 *
 * Will always return the full list of layers passed to it,
 * though if it fails to apply the theme the layers will be
 * returned without any changes applied.
 */
export const tryApplyThemeToLayers = (layers: Layer[], layerStyles: LayerStyle[]): Layer[] => {
  try {
    const restyledLayers = layers.map((layer) => {
      const layerStyle = layerStyles.find((s) => s.layerId === layer.id);
      return {
        ...layer,
        style: layerStyle ?? layer.style,
      };
    });
    return restyledLayers;
  } catch (error) {
    console.error(`Failed to apply map theme`); // eslint-disable-line no-console
    console.error(error); // eslint-disable-line no-console
    return layers;
  }
};

export const getOrderedCategorizedSymbols = (
  layer: Layer,
  featureStatuses: FeatureStatus[],
): CategorizedSymbolWithLayerInfo[] => {
  const { style, id, geomType } = layer;

  const featureStatusesById = _keyBy(featureStatuses, 'id');

  if (!style.categorizedAttributeLabel || !style.symbols) return [];

  const categorizedAttribute = layer.availableAttributes[style.categorizedAttributeLabel];

  const permittedValues = categorizedAttribute?.permittedValues;
  const permittedValueDescriptions = categorizedAttribute?.permittedValueDescriptions ?? {};

  const orderedSymbols = Object.entries(style.symbols)
    .map(([attributeValue, symbol]) => ({
      attributeValue,
      attributeLabel: layer.style.categorizedAttributeLabel,
      description: permittedValueDescriptions && permittedValueDescriptions[attributeValue],
      symbol,
      geomType,
      layerId: id,
    }))
    .sort((a, b) => {
      if (a.attributeValue === 'Other') return 1;
      if (b.attributeValue === 'Other') return -1;

      if (a.symbol.displayOrder && b.symbol.displayOrder) {
        return a.symbol.displayOrder - b.symbol.displayOrder;
      }

      if (!permittedValues) {
        if (isCategorizedByStatus(style.categorizedAttributeLabel)) {
          return (
            featureStatusesById[a.attributeValue].displayOrder -
            featureStatusesById[b.attributeValue].displayOrder
          );
        }

        if (hasThresholdStyling(layer)) return Number(a.attributeValue) - Number(b.attributeValue);

        if (!layer.rasterId) return 0;

        return a.attributeValue.localeCompare(b.attributeValue);
      }

      const l = permittedValues.length;
      const aIndex = permittedValues.findIndex((pv) => pv === a.attributeValue);
      const bIndex = permittedValues.findIndex((pv) => pv === b.attributeValue);

      const aPriority = aIndex === -1 ? l : aIndex;
      const bPriority = bIndex === -1 ? l : bIndex;

      return aPriority - bPriority;
    });

  return orderedSymbols;
};
